import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import {
  PRODUCT_TYPES,
  ProductType,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

type Environment = 'development' | 'test' | 'uat' | 'live';

/**
 * Partner IDs that are designated as private lounge partners per environment.
 * TODO: Move to partner config (products.lounges.is_private_lounge_partner) when roadmap allows.
 */
const PRIVATE_LOUNGE_PARTNER_IDS: Record<Environment, number> = {
  development: 67,
  test: 67,
  uat: 236,
  live: 62,
};

/**
 * Checks if the current partner is a designated private lounge partner.
 *
 * Primary check: Partner ID matches the known private lounge partner for the current environment.
 */
export function isPrivateLoungePartner(): boolean {
  const environment = (
    process.env['NUXT_ENV_ENVIRONMENT'] ||
    process.env['ENVIRONMENT_NAME'] ||
    'live'
  ) as Environment;
  const partnerId = getAppVariable<number>('partner_id', -1);

return PRIVATE_LOUNGE_PARTNER_IDS[environment] === partnerId;
}

/**
 * Exposes helper properties to describe the partner configuration.
 *
 * Note: Describes the partner configuration only - does not take Feature Flags into account.
 */
export function usePartnerConfigHelper (): PartnerConfigHelperProperties {
  const heroProductType = getAppHeroProduct(PRODUCT_TYPES.PARKING);
  const productTypesAvailable = getAppVariable<ProductType[]>('product_types_available', []);
  const secondaryProductTypes = productTypesAvailable.filter((productType) => productType !== heroProductType);

  const isCrossSellFlowEnabled = (() => {
    const parkingAsHeroProduct = heroProductType === PRODUCT_TYPES.PARKING;
    const loungeAsHeroProduct = heroProductType === PRODUCT_TYPES.LOUNGES;
    const fasttrackAsHeroProduct = heroProductType === PRODUCT_TYPES.FASTTRACK;

    return (
      ((parkingAsHeroProduct ||
        loungeAsHeroProduct ||
        fasttrackAsHeroProduct) &&
        (secondaryProductTypes.includes(PRODUCT_TYPES.LOUNGES) ||
          secondaryProductTypes.includes(PRODUCT_TYPES.FASTTRACK) ||
          (secondaryProductTypes.includes(PRODUCT_TYPES.EXTRAS) &&
            getFeatureFlag(
              FEATURE_FLAGS.EM_1149_ENABLE_UNIFIED_EXTRAS_FLOW
            )))) ||
      (secondaryProductTypes.includes(PRODUCT_TYPES.PARKING) &&
        getFeatureFlag(FEATURE_FLAGS.EX_486_AETHER_PARKING_TRAVEL_EXTRA))
    );
  })();

  const isExtrasProductFlowEnabled = (() => {
    return (
      !isCrossSellFlowEnabled &&
      productTypesAvailable.includes(PRODUCT_TYPES.EXTRAS)
    );
  })();

  const isStandalonePartner = (() => {
    return (
      productTypesAvailable.length === 1 &&
      productTypesAvailable[0] === heroProductType
    );
  })();

  const isMultiBasketEnabled = getAppVariable<boolean>('is_multi_basket', false);

  return {
    isCrossSellFlowEnabled,
    isExtrasProductFlowEnabled,
    isStandalonePartner,
    isMultiBasketEnabled,
    secondaryProductTypes,
  };
}

type PartnerConfigHelperProperties = {
  /**
   * True if the cross-sell flow is enabled.
   * The cross-sell flow is enabled when the hero product is either parking or lounges,
   * and at least one of the secondary product types is lounges or fasttrack.
   */
  isCrossSellFlowEnabled: boolean;

  /**
   * True if the extras product flow is enabled.
   * The extras product flow is enabled when the cross sell flow is disabled,
   * and extras is enabled as a product type.
   */
  isExtrasProductFlowEnabled: boolean;

  /**
   * True if the partner is a standalone partner.
   * A standalone partner is one that has only one product type available.
   */
  isStandalonePartner: boolean;

  /**
   * True if multi-basket is enabled via the partner config.
   */
  isMultiBasketEnabled: boolean;

  /**
   * THe `product_types_available` not including the hero product type.
   */
  secondaryProductTypes: ProductType[];
};
